export interface TokenData {
  access_token: string;
  access_expires_in?: number;
  refresh_token: string;
  refresh_expires_in?: number;
}

const API_URL = process.env.REACT_APP_API_URL || "https://api.steelytoe.com";

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  if (match) return decodeURIComponent(match[2]);
  return null;
}

export function getTokenData(): TokenData | null {
  try {
    const dataStr = localStorage.getItem("jwt-token") || getCookie("jwt-token");
    if (dataStr) {
      return JSON.parse(dataStr);
    }
  } catch (e) {
    console.error("Failed to parse token data", e);
  }
  return null;
}

export function getToken(): string | null {
  const data = getTokenData();
  return data ? data.access_token : null;
}

export function getRefreshToken(): string | null {
  const data = getTokenData();
  return data ? data.refresh_token : null;
}

export function setToken(tokenData: TokenData) {
  localStorage.setItem("jwt-token", JSON.stringify(tokenData));
  const cookieValue = encodeURIComponent(JSON.stringify(tokenData));
  const maxAge = tokenData.refresh_expires_in || 604800; // default 7 days in seconds
  if (typeof document !== "undefined") {
    document.cookie = `jwt-token=${cookieValue}; path=/; max-age=${maxAge}; SameSite=Lax`;
  }
}

export function removeToken() {
  localStorage.removeItem("jwt-token");
  if (typeof document !== "undefined") {
    document.cookie = "jwt-token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT";
  }
}

export function isAuthenticated(): boolean {
  return !!getToken();
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

function subscribeTokenRefresh(cb: (token: string) => void) {
  refreshSubscribers.push(cb);
}

function onRefreshed(token: string) {
  refreshSubscribers.forEach((cb) => cb(token));
  refreshSubscribers = [];
}

export async function fetchWithAuth(url: string, options: RequestInit = {}): Promise<Response> {
  const absoluteUrl = url.startsWith("/") ? `${API_URL}${url}` : url;

  const headers = new Headers(options.headers || {});
  const token = getToken();
  if (token) {
    headers.set("Authorization", `Bearer ${token}`);
  }

  const reqOptions: RequestInit = {
    ...options,
    headers,
  };

  let response = await fetch(absoluteUrl, reqOptions);

  if (response.status === 401 && !absoluteUrl.includes("/api/v1/ultimate/refresh")) {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      removeToken();
      if (typeof window !== "undefined") {
        window.location.href = "/login";
      }
      throw new Error("Unauthorized and no refresh token available");
    }

    if (!isRefreshing) {
      isRefreshing = true;
      try {
        const refreshResponse = await fetch(`${API_URL}/api/v1/ultimate/refresh`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });

        if (refreshResponse.ok) {
          const data = await refreshResponse.json();
          const tokenData = data.data || data;
          setToken(tokenData);
          onRefreshed(tokenData.access_token);
        } else {
          removeToken();
          if (typeof window !== "undefined") {
            window.location.href = "/login";
          }
          throw new Error("Failed to refresh token");
        }
      } catch (err) {
        removeToken();
        if (typeof window !== "undefined") {
          window.location.href = "/login";
        }
        throw err;
      } finally {
        isRefreshing = false;
      }
    }

    return new Promise((resolve, reject) => {
      subscribeTokenRefresh((newToken) => {
        headers.set("Authorization", `Bearer ${newToken}`);
        fetch(absoluteUrl, { ...options, headers })
          .then(resolve)
          .catch(reject);
      });
    });
  }

  return response;
}
